import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import axios from "axios";
import { api } from "../../../utils/api";
import Sidebar from "../sidebar";
import ActionButtons from "../InitialKickoff/MaterialTopics/ActionButtons";
import { companyId } from "../../../utils/companyId";
import InputCard from "./InputCard";
import { ProgressContext } from "../ContextAPI";
import { numberError, percentageError } from "../../../utils/validationMessage";
import {
  validateNumber,
  validatePercentage,
} from "../../../utils/validateFields";
import SkipQuestionare from "../../Common/SkipQuestionare";
import InitialQuestionareSidebar from "../../../Layout/InitialQuestionareSidebar";
import { isFilled, isFilledValue } from "../../../utils/common";
import { getQuestionareSubmitted } from "../../../utils/storage";

const WaterManagement = () => {
  const navigate = useNavigate();
  const [isExist, setIsExist] = useState(false);
  const [formData, setFormData] = useState({
    totalWaterWithdrawal: { value: "" },
    totalWaterDischarged: { value: "" },
    totalWasteWaterTreated: { value: "" },
    totalWaterRecycled: { value: "" },
    waterIntensity: { value: "" },
    waterRecycled: { value: "" },
    waterDischargeQuality: { value: "" },
    companyId,
  });
  const { setUpdate } = useContext(ProgressContext);
  const companyDetails = getQuestionareSubmitted();

  const [errors, setErrors] = useState({
    totalWaterWithdrawal: "",
    totalWaterDischarged: "",
    totalWasteWaterTreated: "",
    totalWaterRecycled: "",
    waterIntensity: "",
    waterRecycled: "",
    waterDischargeQuality: "",
  });

  // water data for creating input cards
  const waterData = [
    {
      title: "Total Water Withdrawal",
      placeholder: "1000",
      name: "totalWaterWithdrawal",
      adornmentText: "Cubic meter",
    },
    {
      title: "Total Water Discharged",
      placeholder: "1000",
      name: "totalWaterDischarged",
      adornmentText: "Cubic meter",
    },
    {
      title: "Total Waste Water Treated",
      placeholder: "1000",
      name: "totalWasteWaterTreated",
      adornmentText: "Cubic meter",
    },
    {
      title: "Total Water Recycled/Reused",
      placeholder: "1000",
      name: "totalWaterRecycled",
      adornmentText: "Cubic meter",
    },
    {
      title: "Water Intensity",
      placeholder: "0.5",
      name: "waterIntensity",
      adornmentText: "Water use per unit of output",
    },
    {
      title: "Water Recycled and Reused",
      placeholder: "64",
      name: "waterRecycled",
      adornmentText: "% of total water use",
    },
    {
      title: "Water Discharge Quality",
      placeholder: "8",
      name: "waterDischargeQuality",
      adornmentText: "Compliance with local standards",
    },
  ];

  // navigate back
  const Back = () => {
    navigate("/environment/ghg-emissions");
  };

  // validating input fields
  const validateForm = () => {
    const {
      totalWaterWithdrawal,
      totalWaterDischarged,
      totalWasteWaterTreated,
      totalWaterRecycled,
      waterIntensity,
      waterRecycled,
      waterDischargeQuality,
    } = formData;

    let newErrors = {};

    // Total Water Withdrawal Validation
    if (
      totalWaterWithdrawal.value !== "" &&
      !validateNumber(totalWaterWithdrawal.value)
    ) {
      newErrors.totalWaterWithdrawal = numberError;
    }
    if (
      totalWaterDischarged.value !== "" &&
      !validateNumber(totalWaterDischarged.value)
    ) {
      newErrors.totalWaterDischarged = numberError;
    }
    if (
      totalWasteWaterTreated.value !== "" &&
      !validateNumber(totalWasteWaterTreated.value)
    ) {
      newErrors.totalWasteWaterTreated = numberError;
    }
    if (
      totalWaterRecycled.value !== "" &&
      !validateNumber(totalWaterRecycled.value)
    ) {
      newErrors.totalWaterRecycled = numberError;
    }

    // Water Intensity Validation
    if (waterIntensity.value !== "" && !validateNumber(waterIntensity.value)) {
      newErrors.waterIntensity = numberError;
    }

    // Water Recycled and Reused Validation
    if (
      waterRecycled.value !== "" &&
      !validatePercentage(waterRecycled.value)
    ) {
      newErrors.waterRecycled = percentageError;
    }

    // Water Discharge Quality Validation
    if (
      waterDischargeQuality.value !== "" &&
      !validateNumber(waterDischargeQuality.value)
    ) {
      newErrors.waterDischargeQuality = numberError;
    }

    setErrors(newErrors);

    // Check if there are any errors
    return Object.keys(newErrors).length === 0;
  };

  // fetching data if already exists
  useEffect(() => {
    setUpdate("ESG");
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${api}/environment/water-management/${companyId}`
        );

        if (response.data && response.data.waterData) {
          setFormData(response.data.waterData);
          setIsExist(true);
        }
      } catch (err) {
        console.error("Error:", err);
      }
    };
    fetchData();
  }, []);

  // Save the formData and make the post request
  const Save = async () => {
    if (!validateForm()) {
      return;
    }
    try {
      const {
        totalWaterWithdrawal,
        totalWaterDischarged,
        totalWasteWaterTreated,
        totalWaterRecycled,
        waterIntensity,
        waterRecycled,
        waterDischargeQuality,
      } = formData;

      // ✅ Compute water_management flag
      const water_management =
        isFilledValue(totalWaterWithdrawal) &&
        isFilledValue(totalWaterDischarged) &&
        isFilledValue(totalWasteWaterTreated) &&
        isFilledValue(totalWaterRecycled) &&
        isFilledValue(waterIntensity) &&
        isFilledValue(waterRecycled) &&
        isFilledValue(waterDischargeQuality);

      // ✅ Prepare payload
      const payload = {
        ...formData,
        companyId,
        water_management,
      };
      await axios({
        method: isExist ? "put" : "post",
        url: `${api}/environment/water-management${
          isExist ? `/${companyId}` : ""
        }`,
        data: payload,
      });
      navigate(
        companyDetails?.questionareSubmitted
          ? "/preview"
          : "/environment/waste-management"
      );
    } catch (err) {
      alert(err.response.data.message);
    }
  };

  // Handling the input value
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => {
      return {
        ...prevFormData,
        [name]: {
          ...prevFormData[name],
          value: value,
        },
      };
    });
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" })); // Clear error on change
  };

  return (
    <Box sx={{ display: "flex", backgroundColor: "#EDEDED", height: "100vh" }}>
      <Box
        sx={{
          width: "25vw",
          // overflowY: "auto",
        }}
      >
        <InitialQuestionareSidebar />
      </Box>
      <Box
        sx={{
          p: 2,
          mt: 3,
          width: "75vw",
          height: "100vh", // Full viewport height
          overflowY: "auto", // Allow vertical scrolling if content exceeds viewport
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start", // Adjust to start at the top of the box
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "80%",
            marginBottom: "1rem",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              zIndex: 1,
              padding: "10px 0px",
              borderRadius: "4px",
              alignSelf: "flex-start",
            }}
          >
            ENVIRONMENT: WATER MANAGEMENT
          </Typography>
          <SkipQuestionare />
        </div>
        <Box sx={{ mt: 3, width: "80%" }}>
          {/* Maps the water data */}
          {waterData.map((item, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <InputCard
                title={item.title}
                placeholder={item.placeholder}
                name={item.name}
                value={formData[item.name].value}
                handleChange={handleChange}
                adornmentText={item.adornmentText}
                borderColor={errors[item.name] && "#D34040"}
              />
              {errors[item.name] && (
                <Typography variant="body2" color="error" sx={{ mt: 0 }}>
                  {errors[item.name]}
                </Typography>
              )}
            </Box>
          ))}

          {/* Back, Save Buttons */}
          <ActionButtons
            Back={Back}
            Move={Save}
            disable={companyDetails?.questionareSubmitted}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default WaterManagement;
